import { useState, useEffect, ReactNode } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { Loader2 } from 'lucide-react';
import MfaSetupScreen from './MfaSetupScreen';
import type { UserRole } from '../../types';

// Layer 6 + Layer 7 roles that MUST have MFA enrolled
const MFA_REQUIRED_ROLES: UserRole[] = [
  // Layer 7
  'super_admin', 'it_admin',
  // Layer 6
  'chro', 'cfo', 'cbo', 'ceo_commercial', 'ceo_manufacturing', 'group_ceo', 'coo',
];

interface Props {
  children: ReactNode;
}

/**
 * MfaEnrollmentGate — blocks access for Layer 6/7 users until MFA is enrolled.
 * Place at the top of the React Router structure (wrapping <Routes>).
 *
 * - For MFA-required roles: checks enrollment status, shows MfaSetupScreen if not enrolled
 * - For all other roles: renders children immediately
 */
export default function MfaEnrollmentGate({ children }: Props) {
  const { profile, user } = useAuth();
  const [checking, setChecking] = useState(true);
  const [enrolled, setEnrolled] = useState(false);

  const requiresMfa = profile && MFA_REQUIRED_ROLES.includes(profile.role);

  useEffect(() => {
    if (!requiresMfa || !user) {
      setChecking(false);
      setEnrolled(false);
      return;
    }

    let cancelled = false;

    async function checkEnrollment() {
      try {
        const { data, error } = await supabase.auth.mfa.listFactors();
        if (error) {
          console.error('[MFA] Failed to list factors:', error.message);
          // On error, assume not enrolled (safer — will prompt setup)
          if (!cancelled) { setEnrolled(false); setChecking(false); }
          return;
        }

        // A user is "enrolled" if they have at least one verified TOTP factor
        const hasVerifiedTotp = (data.totp ?? []).some(
          (f: any) => f.status === 'verified',
        );

        if (!cancelled) {
          setEnrolled(hasVerifiedTotp);
          setChecking(false);
        }
      } catch {
        if (!cancelled) { setEnrolled(false); setChecking(false); }
      }
    }

    checkEnrollment();
    return () => { cancelled = true; };
  }, [requiresMfa, user?.id]);

  // Non-MFA roles pass through immediately.
  //
  // A DEV build additionally honours a localStorage flag, so Playwright can drive the E2E
  // super_admin — a role that mandates MFA — without provisioning a TOTP factor.
  //
  // These are not read at runtime: Vite substitutes them with literals at BUILD time, so
  // the `&&` collapses during the build and the minifier deletes the whole expression —
  // the branch, the localStorage read, and the flag's name with it. The bypass is not
  // *disabled* in production; it is *absent*, so there is nothing to switch back on.
  //
  // BOTH conditions are required, and MODE is not redundant. `DEV` is derived from
  // NODE_ENV, so `NODE_ENV=development` — as an env var or a line in `.env.production` —
  // flips it to `true` even under `vite build`, and on its own would put the bypass back
  // into a production bundle (verified). `MODE` comes from `--mode`, which NODE_ENV cannot
  // touch: it is `production` for `vite build` and `development` for the dev server.
  // Flipping MODE means editing the build command, which is a visible, deliberate act.
  //
  // Enforced, not trusted: `npm run build` runs scripts/check-no-mfa-bypass.mjs over the
  // emitted bundle and fails the build — and therefore the deploy — if any trace survives,
  // including when someone forces NODE_ENV. Keep the compile-time checks as the FIRST
  // operands: putting the localStorage read first would leave the flag in the output.
  const e2eDevBypass =
    import.meta.env.MODE === 'development' &&
    import.meta.env.DEV &&
    typeof localStorage !== 'undefined' &&
    localStorage.getItem('__e2e_skip_mfa') === 'true';

  if (!requiresMfa || e2eDevBypass) {
    return <>{children}</>;
  }

  // Checking enrollment status
  if (checking) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-slate-50 dark:bg-slate-950">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-blue-400 mx-auto mb-3" />
          <p className="text-sm text-slate-500 dark:text-slate-400">Checking MFA enrollment...</p>
        </div>
      </div>
    );
  }

  // Not enrolled: force MFA setup.
  // Enrolling runs mfa.challenge + mfa.verify, which upgrades the session to aal2 on the
  // server — so there is nothing to record locally. The old code wrote a device-trust key
  // here that later skipped the challenge outright; that key is gone.
  if (!enrolled) {
    return <MfaSetupScreen onEnrolled={() => setEnrolled(true)} />;
  }

  // Enrolled: render app
  return <>{children}</>;
}
